import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "./ui/dialog";
import { toast } from "sonner";
import {
  Share2, EyeOff, ReceiptText, Download, Printer, Mail, Pencil, Repeat, LogIn,
} from "lucide-react";
import { HeartIcon, ShieldIcon } from "../lib/icons";
import { Input } from "./ui/input";
import { CountUp, ConfettiBurst } from "./Reveal";
import { DonorFieldsSection } from "./DonorForm";
import { ImpactCard } from "./ImpactCard";
import {
  loadProfile, saveProfile, buildReceipt, downloadReceipt, openReceipt,
  ORG_NAME, fmtMoney, emptyDonor, emptyPayment, validateDonorForm,
} from "../lib/donorProfile";
import { useAuth } from "../lib/AuthContext";
import axios from "axios";

import { IMAGES } from "../data/content";

const AMOUNTS = [25, 50, 100, 250];
const PREFS_KEY = "mra_donor_prefs_v1";
const PENDING_RECURRING_KEY = "mra_pending_recurring_v1";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "mra_jwt";
const rgClient = axios.create({ baseURL: API, withCredentials: true });
rgClient.interceptors.request.use((cfg) => {
  const t = typeof window !== "undefined" ? localStorage.getItem(TOKEN_KEY) : null;
  if (t) {
    cfg.headers = cfg.headers || {};
    cfg.headers.Authorization = `Bearer ${t}`;
  }
  return cfg;
});

const loadPrefs = () => {
  if (typeof window === "undefined") return null;
  try { return JSON.parse(window.localStorage.getItem(PREFS_KEY) || "null"); } catch (e) { return null; }
};
const savePrefs = (prefs) => {
  if (typeof window === "undefined") return;
  try { window.localStorage.setItem(PREFS_KEY, JSON.stringify(prefs)); } catch (e) { /* noop */ }
};

const MiniToggle = ({ checked, onChange, testid, label }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    aria-label={label}
    data-testid={testid}
    onClick={() => onChange(!checked)}
    className={`relative inline-flex h-5 w-9 flex-shrink-0 items-center rounded-full transition-colors ${
      checked ? "bg-tangerine" : "bg-ink/20"
    }`}
  >
    <span
      className={`inline-block h-4 w-4 rounded-full bg-bone shadow transition-transform ${
        checked ? "translate-x-[18px]" : "translate-x-0.5"
      }`}
    />
  </button>
);

const ReceiptRow = ({ label, children }) => (
  <div className="flex items-baseline justify-between gap-3 text-[13px]">
    <span className="text-olive uppercase tracking-wide text-[13px]">{label}</span>
    <span className="text-right text-ink">{children}</span>
  </div>
);

export const DonateDialog = ({ open, onOpenChange, raised, goal, studentName, campaignImage, campaignDaysLeft, onComplete }) => {
  const { user } = useAuth();
  const [done, setDone] = useState(false);
  const [amount, setAmount] = useState(50);
  const [custom, setCustom] = useState("");
  const [origin, setOrigin] = useState(null);
  const [snapRaised, setSnapRaised] = useState(0);
  const [secondBurst, setSecondBurst] = useState(false);
  const [flyer, setFlyer] = useState(null);
  const successTotalRef = useRef(null);

  // Feed prefs
  const [boost, setBoost] = useState(false);
  // Recurring gift toggle — converts a one-time gift to a monthly one on
  // the same tap. Signed-in donors get a real POST /api/recurring-gifts
  // row; anonymous donors get a "sign in to activate" nudge in the
  // success view (intent parked in localStorage under mra_pending_recurring_v1).
  const [monthly, setMonthly] = useState(false);
  const [recurringSaved, setRecurringSaved] = useState(false);
  const [showName, setShowName] = useState(true);
  const [showAmount, setShowAmount] = useState(true);
  const [displayName, setDisplayName] = useState("");
  const [remember, setRemember] = useState(false);
  // Tax / pledge acknowledgment — donor must confirm before checkout.
  const [pledgeAck, setPledgeAck] = useState(false);
  // IRS compliance — when giving to a specific student, donor must
  // confirm three things: (1) student is not a tax dependent, (2)
  // student is not an immediate family member, (3) donor is receiving
  // no goods/services/personal benefit in return.
  const [dependentAck, setDependentAck] = useState(false);
  const [quidProQuoAck, setQuidProQuoAck] = useState(false);
  const [ackError, setAckError] = useState(false);

  // Donor + payment
  const [profile, setProfile] = useState(null);
  const [editingProfile, setEditingProfile] = useState(false);
  const [donor, setDonor] = useState(emptyDonor());
  const [payment, setPayment] = useState(emptyPayment());
  const [formErrors, setFormErrors] = useState({});

  // Receipt snapshot for success view
  const [receipt, setReceipt] = useState(null);
  const [confirmed, setConfirmed] = useState(null);

  const value = custom ? Number(custom) || 0 : amount;
  const boostAmount = Math.round(value * 5) / 100;
  const total = value + (boost ? boostAmount : 0);
  const hasCampaign = typeof raised === "number";
  const campaignName = hasCampaign ? `${studentName}'s Campaign` : `${ORG_NAME} Fund`;

  // Load persisted profile + prefs on open.
  useEffect(() => {
    if (!open) return;
    const p = loadProfile();
    setProfile(p);
    setEditingProfile(!p);
    setDonor(p ? {
      fullName: p.fullName || "",
      email: p.email || "",
      phone: p.phone || "",
      street: p.street || "",
      city: p.city || "",
      state: p.state || "",
      zip: p.zip || "",
      undergrad: p.undergrad || "",
      grad: p.grad || "",
    } : emptyDonor());
    setPayment(emptyPayment());
    setFormErrors({});

    const prefs = loadPrefs();
    if (prefs) {
      setShowName(prefs.showName !== false);
      setShowAmount(prefs.showAmount !== false);
      setBoost(!!prefs.boost);
      setDisplayName(prefs.displayName || (p ? p.fullName : ""));
      setRemember(true);
    } else {
      setShowName(true);
      setShowAmount(true);
      setBoost(false);
      setDisplayName(p ? p.fullName : "");
      setRemember(!!p);
    }
    // Reset the pledge acknowledgment on every open — donors must
    // affirm the tax + pledge summary each session.
    setPledgeAck(false);
    setDependentAck(false);
    setAckError(false);
    // Force monthly OFF whenever we open in campaign context — 30-day
    // student campaigns don't support perpetual recurring gifts. The
    // toggle itself is also hidden below, but this guards against stale
    // state if the dialog is opened first for the general fund and then
    // re-opened against a specific student campaign.
    if (typeof raised === "number") setMonthly(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Reset picker/state a beat after dialog closes.
  useEffect(() => {
    if (open) return undefined;
    const t = setTimeout(() => {
      setDone(false);
      setCustom("");
      setAmount(50);
      setOrigin(null);
      setSecondBurst(false);
      setFlyer(null);
      setConfirmed(null);
      setReceipt(null);
      setFormErrors({});
      setMonthly(false);
      setRecurringSaved(false);
    }, 250);
    return () => clearTimeout(t);
  }, [open]);

  // Secondary confetti sprinkle
  useEffect(() => {
    if (!done) return undefined;
    const t = setTimeout(() => setSecondBurst(true), 900);
    return () => clearTimeout(t);
  }, [done]);

  // Flyer measurement
  useEffect(() => {
    if (!done || !flyer || flyer.to) return undefined;
    const raf = requestAnimationFrame(() => {
      const el = successTotalRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      setFlyer((f) => (f && !f.to ? { ...f, to: { x: r.left + r.width / 2, y: r.top + r.height / 2 } } : f));
    });
    return () => cancelAnimationFrame(raf);
  }, [done, flyer]);

  useEffect(() => {
    if (!flyer || !flyer.to) return undefined;
    const t = setTimeout(() => setFlyer(null), 850);
    return () => clearTimeout(t);
  }, [flyer]);

  const close = () => onOpenChange(false);

  const share = async () => {
    try { await navigator.clipboard.writeText(window.location.href); } catch (e) { /* noop */ }
    toast.success("Link copied!", { description: "Share it anywhere to help reach the goal faster." });
  };

  // Single-click donation confirm. Validates donor info if new/editing, else uses saved.
  const confirmDonation = (e) => {
    const cx = e && e.clientX ? e.clientX : window.innerWidth / 2;
    const cy = e && e.clientY ? e.clientY : window.innerHeight / 2;
    const clickPoint = { x: cx, y: cy };

    // If using a saved profile without editing, we bypass form validation.
    const usingSaved = profile && !editingProfile;
    let usedDonor;
    let paymentLast4;

    // Tax + pledge acknowledgment must be checked before checkout.
    // When giving to a specific student, three compliance acks are
    // required in addition to the base pledge acknowledgment:
    // (1) preference / non-binding routing,
    // (2) not a tax dependent AND not an immediate family member,
    // (3) receiving no goods, services, or personal benefit in return.
    if (!pledgeAck || (hasCampaign && (!dependentAck || !quidProQuoAck))) {
      setAckError(true);
      setTimeout(() => {
        const el = document.querySelector('[data-testid="pledge-ack-card"]');
        if (el && el.scrollIntoView) el.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 30);
      return;
    }

    if (usingSaved) {
      usedDonor = profile;
      paymentLast4 = profile.lastPaymentLast4 || "----";
    } else {
      const errors = validateDonorForm(donor, payment);
      if (Object.keys(errors).length) {
        setFormErrors(errors);
        // Scroll first error into view
        setTimeout(() => {
          const el = document.querySelector('[data-testid="donor-fields"] .text-scarlet');
          if (el && el.scrollIntoView) el.scrollIntoView({ behavior: "smooth", block: "center" });
        }, 30);
        return;
      }
      setFormErrors({});
      usedDonor = { ...donor };
      paymentLast4 = payment.card.replace(/\s/g, "").slice(-4);
    }

    // Persist if opted in
    if (remember) {
      if (!usingSaved) {
        const toSave = { ...usedDonor, lastPaymentLast4: paymentLast4 };
        saveProfile(toSave);
        setProfile(toSave);
      }
      savePrefs({ boost, showName, showAmount, displayName: displayName || usedDonor.fullName });
    }

    // Fire flyer + confetti + build receipt + switch to Thank-You
    setOrigin(clickPoint);
    setSnapRaised(hasCampaign ? raised : 0);
    setFlyer({ from: clickPoint, amount: value || 0 });
    setConfirmed({ base: value, boostAmount, total, boost });

    // TODO(backend): Replace client-side receipt build + confetti-only flow
    //   with a real Stripe payment intent + server-issued receipt.
    //     1. POST /api/donations  { campaignId, amount, boost, donor, paymentMethodId }
    //     2. Stripe confirms via webhook -> backend emits receipt PDF + email.
    //     3. Return { receiptId, receiptUrl, updatedRaised } to the client.
    //   Skip all of the above when `isDemoMode()` returns true (see lib/demoMode.js).
    const r = buildReceipt({
      donation: { amount: value, boost, boostAmount, total },
      donor: usedDonor,
      paymentLast4,
      campaignName,
    });
    setReceipt(r);
    setDone(true);

    if (onComplete) {
      onComplete({
        amount: value,
        boost,
        boostAmount,
        total,
        showName,
        showAmount,
        displayName: showName ? (displayName.trim() || usedDonor.fullName || "You") : "Anonymous",
      });
    }

    if (usedDonor.email) {
      toast.success("Receipt sent", {
        description: `A copy is on its way to ${usedDonor.email}.`,
        icon: <Mail className="h-4 w-4" />,
      });
    }

    // "Make this monthly" side-effect.
    if (monthly) {
      const isDonor = user?.role === "donor";
      if (isDonor) {
        // Signed-in donor — persist the recurring gift for real.
        rgClient
          .post("/recurring-gifts", {
            amount_cents: Math.round(value * 100),
            cadence: "monthly",
            preference_label: hasCampaign ? `${studentName}'s campaign` : null,
            note: hasCampaign ? `Started from ${studentName}'s campaign page` : "Started from Fund page",
          })
          .then(() => {
            setRecurringSaved(true);
            toast.success("Recurring gift saved.", {
              description: "Manage it any time from your donor dashboard.",
              icon: <Repeat className="h-4 w-4" />,
            });
          })
          .catch(() => {
            toast.error("Couldn't save the recurring gift.", {
              description: "Your one-time gift went through. Try turning on monthly from your dashboard.",
            });
          });
      } else {
        // Anonymous — park the intent so we can prompt post-signin.
        try {
          localStorage.setItem(
            PENDING_RECURRING_KEY,
            JSON.stringify({
              amount_cents: Math.round(value * 100),
              cadence: "monthly",
              preference_label: hasCampaign ? `${studentName}'s campaign` : null,
              parked_at: new Date().toISOString(),
            })
          );
        } catch { /* ignore */ }
      }
    }
  };

  const usingSaved = profile && !editingProfile;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent
          data-testid="donate-dialog"
          className="max-w-md overflow-hidden border-border bg-sand p-0 gap-0 sm:rounded-lg max-h-[92vh] flex flex-col"
        >
        {!done ? (
          <div className="overflow-y-auto">
            <div className="relative overflow-hidden bg-beige px-8 py-9 text-ink">
              <img
                src={IMAGES.logoColor}
                alt="My Rich Aunt"
                className="relative h-20 w-auto mix-blend-multiply"
                data-testid="donate-header-logo"
              />
              <DialogTitle className="relative mt-5 font-serif text-3xl font-normal text-ink">
                {hasCampaign ? `Support ${studentName}'s Campaign` : `Give to the ${ORG_NAME} Fund`}
              </DialogTitle>
              <DialogDescription className="relative mt-2 text-[13px] text-olive">
                {hasCampaign
                  ? `Your donation is tax-deductible.`
                  : `Not sure who to back? Give to the general fund and My Rich Aunt will award grants to the verified students who need them most. Every gift is tax-deductible.`}
              </DialogDescription>
            </div>

            <div className="px-8 py-8 space-y-5">
              {/* Amount */}
              <div>
                <div className="grid grid-cols-4 gap-3">
                  {AMOUNTS.map((a) => (
                    <button
                      key={a}
                      data-testid={`amount-${a}`}
                      onClick={() => { setAmount(a); setCustom(""); }}
                      className={`rounded-md border py-3 text-base font-semibold transition-colors ${ !custom && amount === a ? "border-ink bg-ink text-sand" : "border-border bg-bone text-ink hover:border-ink" }`}
                    >
                      ${a}
                    </button>
                  ))}
                </div>
                <div className="mt-3">
                  <Input
                    data-testid="custom-amount"
                    type="number"
                    value={custom}
                    onChange={(e) => setCustom(e.target.value)}
                    placeholder="Custom amount"
                  />
                </div>
              </div>

              {/* 5% overhead boost */}
              <label
                data-testid="boost-row"
                className={`flex cursor-pointer items-start justify-between gap-4 rounded-md border p-4 transition-colors ${
                  boost ? "border-tangerine bg-tangerine/8" : "border-border bg-bone"
                }`}
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-2 text-[13px] font-semibold text-ink">
                    <HeartIcon className={`h-4 w-4 ${boost ? "text-tangerine" : "text-olive"}`} />
                    Cover 5% platform fee
                  </div>
                  <p className="mt-1 text-[13px] leading-relaxed text-olive">
                    Adds <span className="font-semibold text-ink">{fmtMoney(boostAmount)}</span> so 100% of your gift funds student grants.
                  </p>
                </div>
                <MiniToggle checked={boost} onChange={setBoost} testid="boost-toggle" label="Cover 5% platform fee" />
              </label>

              {/* Become a sustaining donor — reframes the monthly toggle
                  as an identity, not a checkbox. Hidden when giving to a
                  specific student campaign — those are 30-day campaigns
                  (with a possible 15-day extension) so a perpetual monthly
                  gift doesn't make sense. Monthly is still offered for
                  General Fund gifts so donors can keep the fund working
                  year-round, between campaigns. */}
              {!hasCampaign && (
                <label
                data-testid="monthly-row"
                className={`flex cursor-pointer items-start justify-between gap-4 rounded-md border p-4 transition-colors ${
                  monthly ? "border-cobalt bg-cobalt/8" : "border-border bg-bone"
                }`}
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-2 text-[13px] font-semibold text-ink">
                    <Repeat className={`h-4 w-4 ${monthly ? "text-cobalt" : "text-olive"}`} strokeWidth={2} />
                    Become a sustaining donor
                  </div>
                  <p className="mt-1 text-[13px] leading-relaxed text-olive" data-testid="monthly-explainer">
                    {monthly
                      ? <>You'll give <span className="font-semibold text-ink">{fmtMoney(total)}</span> today and every month after. Sustaining donors keep the General Fund ready between campaigns. Change or cancel any time.</>
                      : "Monthly gifts sustain the General Fund all year — so we can meet a student's need the day it comes up, not the day a campaign closes."}
                  </p>
                  {monthly && !user && (
                    <p className="mt-2 text-[13px] font-medium text-cobalt" data-testid="monthly-signin-hint">
                      We'll ask you to <Link to="/login" className="underline underline-offset-2 hover:text-ink">sign in with Google</Link> after this gift to activate your sustaining cadence.
                    </p>
                  )}
                </div>
                <MiniToggle checked={monthly} onChange={setMonthly} testid="monthly-toggle" label="Become a sustaining donor" />
              </label>
              )}

              {/* Feed preferences */}
              <div data-testid="feed-prefs" className="rounded-md border border-border bg-bone p-4">
                <div className="flex items-center gap-2 text-[13px] font-semibold text-ink">
                  <EyeOff className="h-4 w-4 text-olive" strokeWidth={2} />
                  Donation feed
                </div>
                {showName && (
                  <Input
                    data-testid="donor-display-name"
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    placeholder="Display name (optional)"
                    className="mt-3"
                  />
                )}
                <label className="mt-3 flex cursor-pointer items-center justify-between text-[13px] text-ink">
                  <span>Show my name{showName && displayName ? ` (${displayName})` : ""}</span>
                  <MiniToggle checked={showName} onChange={setShowName} testid="show-name-toggle" label="Show my name in the feed" />
                </label>
                <label className="mt-2.5 flex cursor-pointer items-center justify-between text-[13px] text-ink">
                  <span>Show my donation amount</span>
                  <MiniToggle checked={showAmount} onChange={setShowAmount} testid="show-amount-toggle" label="Show my donation amount" />
                </label>
              </div>

              {/* Donor info — compact summary or full form */}
              {usingSaved ? (
                <div data-testid="saved-donor-card" className="flex items-start justify-between gap-3 rounded-md border border-grass/30 bg-grass/8 p-4">
                  <div className="min-w-0">
                    <div className="flex items-center gap-1.5 text-[13px] font-semibold uppercase tracking-wide text-grass">
                      <ShieldIcon className="h-3.5 w-3.5" />
                      Using your saved info
                    </div>
                    <p className="mt-1.5 text-[13px] font-semibold text-ink truncate">{profile.fullName}</p>
                    <p className="text-[13px] text-olive truncate">{profile.email}</p>
                    <p className="mt-0.5 text-[13px] text-olive">
                      Card ending <span className="font-mono">•••• {profile.lastPaymentLast4 || "----"}</span>
                    </p>
                  </div>
                  <button
                    data-testid="edit-donor-info"
                    onClick={() => setEditingProfile(true)}
                    className="inline-flex items-center gap-1 rounded-full border border-ink/25 bg-bone px-3 py-1.5 text-[13px] font-semibold text-ink transition-colors hover:border-ink"
                  >
                    <Pencil className="h-3 w-3" strokeWidth={2} /> Edit
                  </button>
                </div>
              ) : (
                <div className="rounded-md border border-border bg-bone p-4">
                  <div>
                    <h3 className="text-[13px] font-semibold text-ink">Your info</h3>
                    <p className="mt-0.5 text-[13px] text-olive">Required for your tax-deductible receipt.</p>
                  </div>
                  <div className="mt-4">
                    <DonorFieldsSection
                      donor={donor}
                      payment={payment}
                      errors={formErrors}
                      onDonorChange={setDonor}
                      onPaymentChange={setPayment}
                    />
                  </div>
                  {profile && (
                    <button
                      onClick={() => setEditingProfile(false)}
                      className="mt-3 text-[13px] font-semibold text-olive underline underline-offset-2 hover:text-ink"
                    >
                      Cancel edit and use saved info
                    </button>
                  )}
                </div>
              )}

              {/* Remember checkbox */}
              <label className="flex cursor-pointer items-center gap-2.5 rounded-md border border-border bg-secondary/40 px-4 py-3">
                <input
                  data-testid="remember-prefs"
                  type="checkbox"
                  checked={remember}
                  onChange={(e) => setRemember(e.target.checked)}
                  className="h-4 w-4 rounded border-border accent-tangerine"
                />
                <span className="text-[13px] text-ink">Remember my preferences on this device.</span>
              </label>

              {/* Pledge + tax-deductible acknowledgment — reinforces where the
                  money goes and how it's routed. Required before checkout. */}
              <div
                data-testid="pledge-ack-card"
                className={`rounded-md border p-4 transition-colors ${
                  ackError && !pledgeAck ? "border-scarlet bg-scarlet/6" : "border-cobalt/25 bg-cobalt/6"
                }`}
              >
                <div className="flex items-center gap-2 text-[13px] font-semibold uppercase tracking-[0.18em] text-cobalt">
                  <ShieldIcon className="h-3.5 w-3.5" />
                  Before you give
                </div>
                <p className="mt-3 text-base leading-relaxed text-ink">
                  Your gift is made to <span className="font-semibold">{ORG_NAME}</span>, a 501(c)(3) nonprofit,
                  and is fully tax-deductible. Your donation supports {ORG_NAME}'s{" "}
                  <span className="font-semibold text-ink">supplemental educational grant program</span>.
                  The preferences you set above are a non-binding indication of which
                  {hasCampaign ? ` student's` : ""} verified grant requests you hope to help fund — {ORG_NAME} retains full discretion over how funds are awarded.
                </p>
                <label className="mt-4 flex cursor-pointer items-start gap-2.5">
                  <input
                    data-testid="pledge-ack-checkbox"
                    type="checkbox"
                    checked={pledgeAck}
                    onChange={(e) => {
                      setPledgeAck(e.target.checked);
                      if (e.target.checked) setAckError(false);
                    }}
                    className="mt-0.5 h-4 w-4 flex-shrink-0 rounded border-border accent-tangerine"
                  />
                  <span className="text-[13px] leading-snug text-ink">
                    I understand my donation supports {ORG_NAME}'s supplemental educational grant program. My preferences above are a non-binding indication of which student(s) I hope {ORG_NAME} will fund; final grant decisions rest with {ORG_NAME}.
                  </span>
                </label>

                {hasCampaign && (
                  <>
                    <label className="mt-3 flex cursor-pointer items-start gap-2.5">
                      <input
                        data-testid="dependent-ack-checkbox"
                        type="checkbox"
                        checked={dependentAck}
                        onChange={(e) => {
                          setDependentAck(e.target.checked);
                          if (e.target.checked) setAckError(false);
                        }}
                        className="mt-0.5 h-4 w-4 flex-shrink-0 rounded border-border accent-tangerine"
                      />
                      <span className="text-[13px] leading-snug text-ink">
                        I confirm {studentName} is <span className="font-semibold">not</span> claimed as a dependent on my federal or state income tax returns, and is <span className="font-semibold">not</span> an immediate family member of mine.
                      </span>
                    </label>

                    <label className="mt-3 flex cursor-pointer items-start gap-2.5">
                      <input
                        data-testid="quid-pro-quo-ack-checkbox"
                        type="checkbox"
                        checked={quidProQuoAck}
                        onChange={(e) => {
                          setQuidProQuoAck(e.target.checked);
                          if (e.target.checked) setAckError(false);
                        }}
                        className="mt-0.5 h-4 w-4 flex-shrink-0 rounded border-border accent-tangerine"
                      />
                      <span className="text-[13px] leading-snug text-ink">
                        I am <span className="font-semibold">not</span> receiving any goods, services, or personal benefit in return for this contribution.
                      </span>
                    </label>
                  </>
                )}

                {ackError && (!pledgeAck || (hasCampaign && (!dependentAck || !quidProQuoAck))) && (
                  <p
                    data-testid="pledge-ack-error"
                    className="mt-2 text-[13px] font-medium text-scarlet"
                  >
                    Please acknowledge {hasCampaign ? "all three statements" : ""} before continuing.
                  </p>
                )}
              </div>

              {/* Confirm button */}
              <motion.button
                data-testid="donate-confirm"
                onClick={confirmDonation}
                whileTap={{ scale: 0.96 }}
                transition={{ duration: 0.12, ease: [0.22, 0.61, 0.36, 1] }}
                className="w-full rounded-full bg-ink py-4 text-base font-semibold text-sand transition-colors hover:bg-forest"
              >
                {monthly ? (
                  <>
                    Donate {fmtMoney(total)} <span className="opacity-70">then every month</span>
                  </>
                ) : (
                  <>Donate {fmtMoney(total)}</>
                )}
                {boost && !monthly && (
                  <span className="ml-2 text-[13px] font-medium text-sand/70">
                    ({fmtMoney(value)} + {fmtMoney(boostAmount)} boost)
                  </span>
                )}
              </motion.button>
              <p className="text-center text-[13px] text-olive">Prototype only. No charge will be made.</p>
            </div>
          </div>
        ) : (
          <div className="relative overflow-y-auto" data-testid="donate-success">
            <ConfettiBurst origin={origin} />
            {secondBurst && <ConfettiBurst origin={origin} count={28} />}
            <div className="relative overflow-hidden bg-yellow px-8 py-9 text-center text-ink">
              <div className="relative mx-auto flex h-24 w-24 items-center justify-center">
                <motion.span
                  aria-hidden
                  className="pointer-events-none absolute inset-0 rounded-full bg-tangerine/60"
                  animate={{ scale: [1, 1.6, 1], opacity: [0.5, 0, 0.5] }}
                  transition={{ duration: 2.4, ease: "easeOut", repeat: Infinity }}
                />
                <img
                  src={IMAGES.logoColor}
                  alt="My Rich Aunt"
                  className="relative h-20 w-auto mix-blend-multiply"
                  data-testid="donate-success-logo"
                />
              </div>
              <DialogTitle className="relative mt-4 font-serif text-3xl font-normal text-ink">Thank you!</DialogTitle>
              <DialogDescription className="relative mt-2 text-[13px] text-ink/75">
                {hasCampaign
                  ? `${studentName}'s campaign just moved closer to the goal.`
                  : `Your gift joins the ${ORG_NAME} Fund. We'll get it to the students who need it most.`}
              </DialogDescription>
            </div>
            <div className="relative z-10 bg-sand px-8 py-8 text-center">
              <p className="text-[13px] uppercase tracking-[0.18em] text-tangerine">{hasCampaign ? "New total raised" : "Your pledge"}</p>
              <motion.p
                ref={successTotalRef}
                className="mt-2 font-serif text-3xl lg:text-[3.5rem] leading-none text-tangerine"
                data-testid="donate-success-total"
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.4, delay: 0.08, ease: [0.22, 0.61, 0.36, 1] }}
              >
                <CountUp startOnMount from={snapRaised} to={snapRaised + (value || 0)} prefix="$" duration={1.8} />
              </motion.p>
              <p className="mt-4 text-[13px] text-olive">
                Your {fmtMoney(confirmed ? confirmed.total : total)} pledge is part of our prototype. Real payments arrive at launch.
              </p>
              {confirmed && confirmed.boost && (
                <div
                  data-testid="boost-ack"
                  className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-tangerine/12 px-3 py-1 text-[13px] font-semibold text-tangerine"
                >
                  <HeartIcon className="h-3.5 w-3.5" />
                  Includes {fmtMoney(confirmed.boostAmount)} toward MRA overhead. Thank you.
                </div>
              )}

              {/* Monthly follow-up */}
              {monthly && recurringSaved && (
                <motion.div
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mt-4 flex items-start gap-3 rounded-md border border-cobalt/40 bg-cobalt/6 p-4 text-left"
                  data-testid="donate-recurring-saved"
                >
                  <Repeat className="mt-0.5 h-5 w-5 text-cobalt" strokeWidth={2} />
                  <div className="min-w-0">
                    <p className="text-[13px] font-semibold text-ink">You're a sustaining donor.</p>
                    <p className="mt-1 text-[13px] leading-relaxed text-olive">
                      We'll give the same amount every month on your behalf, keeping the General Fund ready year-round.{" "}
                      <Link to="/recurring-gifts" className="font-semibold text-ink underline underline-offset-2 hover:text-cobalt">
                        Manage it here
                      </Link>.
                    </p>
                  </div>
                </motion.div>
              )}
              {monthly && !recurringSaved && !user && (
                <motion.div
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mt-4 flex items-start gap-3 rounded-md border-2 border-cobalt bg-cobalt/[0.06] p-4 text-left"
                  data-testid="donate-recurring-nudge"
                >
                  <Repeat className="mt-0.5 h-5 w-5 text-cobalt" strokeWidth={2} />
                  <div className="min-w-0 flex-1">
                    <p className="text-[13px] font-semibold text-ink">One more tap to become a sustaining donor.</p>
                    <p className="mt-1 text-[13px] leading-relaxed text-olive">
                      Sign in with Google and we'll activate your monthly cadence automatically. Same amount, same fund, month after month.
                    </p>
                    <Link
                      to="/login"
                      data-testid="donate-recurring-signin"
                      className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-ink px-4 py-2 text-[13px] font-semibold text-sand transition-colors hover:bg-forest"
                    >
                      <LogIn className="h-3.5 w-3.5" strokeWidth={2} />
                      Sign in to activate
                    </Link>
                  </div>
                </motion.div>
              )}

              {confirmed && (
                <ImpactCard
                  hasCampaign={hasCampaign}
                  studentName={studentName}
                  campaignImage={campaignImage}
                  campaignGoal={goal}
                  campaignRaisedAfter={hasCampaign ? snapRaised + confirmed.total : 0}
                  campaignDaysLeft={campaignDaysLeft}
                  amount={confirmed.total}
                  showAmount={showAmount}
                  showName={showName}
                  displayName={(displayName || "").trim() || (usingSaved ? (profile && profile.fullName) : donor.fullName) || ""}
                />
              )}

              {receipt && (
                <div data-testid="receipt-card" className="mt-6 rounded-lg border border-border bg-bone p-5 text-left">
                  <div className="flex items-center gap-2">
                    <span className="flex h-8 w-8 items-center justify-center rounded-full bg-tangerine/12">
                      <ReceiptText className="h-4 w-4 text-tangerine" strokeWidth={2} />
                    </span>
                    <div>
                      <p className="text-[13px] font-semibold text-ink">Your tax-deductible receipt</p>
                      <p className="text-[13px] text-olive">Also emailed to {receipt.donor?.email || "you"}.</p>
                    </div>
                  </div>
                  <dl className="mt-4 space-y-1.5">
                    <ReceiptRow label="Organization">{receipt.orgName}</ReceiptRow>
                    <ReceiptRow label="EIN"><span data-testid="receipt-ein">{receipt.ein}</span></ReceiptRow>
                    <ReceiptRow label="Date">{receipt.date}</ReceiptRow>
                    <ReceiptRow label="Amount"><span className="font-bold text-ink">{fmtMoney(receipt.donation.total)}</span></ReceiptRow>
                    <ReceiptRow label="Paid with">Card •••• {receipt.paymentLast4 || "----"}</ReceiptRow>
                    <ReceiptRow label="Receipt #"><span className="font-mono text-[13px]">{receipt.id}</span></ReceiptRow>
                  </dl>
                  <p className="mt-4 border-t border-border pt-3 text-[13px] leading-relaxed text-olive">
                    {ORG_NAME} did not provide any goods or services in exchange for this gift. Your donation is tax-deductible to the fullest extent allowed by law.
                  </p>
                  <div className="mt-4 flex gap-2">
                    <button
                      data-testid="receipt-view"
                      onClick={() => openReceipt(receipt)}
                      className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-full border border-ink/20 bg-bone px-3 py-2 text-[13px] font-semibold text-ink transition-colors hover:border-ink"
                    >
                      <Printer className="h-3.5 w-3.5" strokeWidth={2} /> View or print
                    </button>
                    <button
                      data-testid="receipt-download"
                      onClick={() => downloadReceipt(receipt)}
                      className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-full bg-tangerine px-3 py-2 text-[13px] font-semibold text-sand transition-colors hover:bg-tangerine/90"
                    >
                      <Download className="h-3.5 w-3.5" strokeWidth={2} /> Download
                    </button>
                  </div>
                </div>
              )}

              <div className="mt-6 flex flex-col gap-3">
                <button
                  data-testid="donate-share"
                  onClick={share}
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-ink py-3.5 text-base font-semibold text-sand transition-colors hover:bg-forest"
                >
                  <Share2 className="h-4 w-4" strokeWidth={2} />
                  {hasCampaign ? "Share this campaign" : `Share ${ORG_NAME}`}
                </button>
                <button
                  data-testid="donate-done"
                  onClick={close}
                  className="rounded-full border border-ink/20 bg-bone py-3.5 text-base font-semibold text-ink transition-colors hover:border-ink"
                >
                  Done
                </button>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
      {flyer && flyer.to && typeof document !== "undefined" && createPortal(
        <motion.div
          aria-hidden
          className="pointer-events-none fixed left-0 top-0 z-[110] inline-flex items-center gap-1.5 rounded-full bg-tangerine px-3 py-1.5 text-[13px] font-semibold text-sand shadow-[0_10px_30px_-8px_rgba(253,97,6,0.6)]"
          initial={{ x: flyer.from.x - 40, y: flyer.from.y - 20, scale: 0.7, opacity: 0, rotate: -6 }}
          animate={{
            x: [flyer.from.x - 40, (flyer.from.x + flyer.to.x) / 2 - 40, flyer.to.x - 40],
            y: [flyer.from.y - 20, Math.min(flyer.from.y, flyer.to.y) - 140, flyer.to.y - 20],
            scale: [0.7, 1.05, 0.35],
            opacity: [0, 1, 0],
            rotate: [-6, 4, -2],
          }}
          transition={{ duration: 0.75, ease: [0.34, 1.4, 0.64, 1], times: [0, 0.45, 1] }}
        >
          <HeartIcon className="h-4 w-4 text-sand" />
          ${flyer.amount}
        </motion.div>,
        document.body,
      )}
    </Dialog>
  );
};
